import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Copy, Check, Share2, Sparkles, Trophy } from "lucide-react";
import api from "@/lib/api";
import usePromoStatus from "@/lib/usePromoStatus";

/**
 * Referral progress card — shows the user's personal share link, live
 * progress toward the 3-activated-referrals threshold, and the coupon
 * state (unlocked / redeemed / working toward). Used on both the Account
 * page and (in a slimmer variant) inside the Dashboard upgrade banner.
 */
export default function ReferralProgress({ compact = false }) {
  const { t } = useTranslation();
  const { params: P } = usePromoStatus();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return null;

  const PUBLIC_SITE =
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    "https://peakmarketsociety.com";
  const shareUrl = `${PUBLIC_SITE}/?ref=${stats.code}`;
  const pct = Math.min(100, Math.round((stats.referred_activated / stats.required) * 100));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("referralCard.linkCopied"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("referralCard.copyFail"));
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t("referralCard.shareTitle"),
          text: t("referralCard.shareText"),
          url: shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const stateChip = () => {
    if (stats.coupon_redeemed) {
      return (
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-slate-200 text-slate-700 rounded-full px-2.5 py-1">
          <Check size={11} /> {t("referralCard.state.redeemed")}
        </span>
      );
    }
    if (stats.coupon_unlocked) {
      return (
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-500 text-white rounded-full px-2.5 py-1">
          <Trophy size={11} /> {t("referralCard.state.unlocked", { pct: stats.discount_percent })}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-800 rounded-full px-2.5 py-1">
        <Sparkles size={11} /> {t("referralCard.state.remaining", { count: stats.remaining })}
      </span>
    );
  };

  return (
    <div
      data-testid="referral-progress"
      className={`bg-white border border-slate-200 rounded-2xl ${compact ? "p-5" : "p-6 sm:p-8"}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-blue-800 font-bold">
            {t("referralCard.eyebrow")}
          </div>
          <h3 className={`font-display font-bold ${compact ? "text-lg" : "text-2xl"} text-slate-900 tracking-tight mt-1`}>
            {t("referralCard.title", P)}
          </h3>
          {!compact && (
            <p className="text-sm text-slate-600 mt-1 max-w-lg">
              {t("referralCard.body")}
            </p>
          )}
        </div>
        {stateChip()}
      </div>

      {/* Progress bar */}
      <div className="mt-6">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-sm font-semibold text-slate-900">
            {t("referralCard.progress.activated", { done: stats.referred_activated, required: stats.required })}
          </div>
          <div className="text-xs text-slate-500">
            {t("referralCard.progress.signedPaid", { total: stats.referred_total, paid: stats.referred_paid })}
          </div>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            data-testid="referral-progress-bar"
            className={`h-full rounded-full transition-all duration-500 ${
              stats.coupon_unlocked ? "bg-emerald-500" : "bg-gradient-to-r from-blue-700 to-amber-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Share block */}
      <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-2">
          {t("referralCard.code.label")}
        </div>
        <div
          data-testid="referral-display-code"
          className="font-mono text-2xl font-black tracking-widest text-slate-900 bg-white border-2 border-dashed border-amber-400 rounded-lg px-4 py-3 text-center select-all"
        >
          {stats.display_code || `PMS-${(stats.code || "").toUpperCase()}`}
        </div>

        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold mt-4 mb-2">
          {t("referralCard.code.orShare")}
        </div>
        <div className="flex items-center gap-2">
          <code
            data-testid="referral-share-url"
            className="flex-1 min-w-0 truncate text-sm text-slate-900 bg-white border border-slate-200 rounded-md px-3 py-2 font-mono"
          >
            {shareUrl}
          </code>
          <button
            type="button"
            onClick={copy}
            data-testid="referral-copy-button"
            className="shrink-0 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? t("referralCard.copied") : t("referralCard.copy")}
          </button>
          <button
            type="button"
            onClick={share}
            data-testid="referral-share-button"
            className="shrink-0 inline-flex items-center gap-1.5 bg-blue-800 hover:bg-blue-700 text-white text-sm font-semibold px-3 py-2 rounded-md"
          >
            <Share2 size={14} /> {t("referralCard.share")}
          </button>
        </div>
      </div>

      {stats.coupon_unlocked && !compact && (
        <Link
          to="/checkout/agreement/premium"
          data-testid="referral-cta-checkout"
          className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-5 py-2.5 rounded-md"
        >
          <Trophy size={14} /> {t("referralCard.claim")}
        </Link>
      )}

      {/* Coupon preview — always visible (locked and unlocked). */}
      {!stats.coupon_redeemed && stats.premium_price_after != null && (
        <div
          data-testid="referral-coupon-preview"
          className={`mt-5 rounded-xl border p-4 ${
            stats.coupon_unlocked
              ? "border-emerald-300 bg-emerald-50"
              : "border-amber-300 bg-amber-50/60"
          }`}
        >
          <div className={`text-[10px] uppercase tracking-[0.24em] font-bold ${
            stats.coupon_unlocked ? "text-emerald-700" : "text-amber-800"
          }`}>
            {stats.coupon_unlocked ? t("referralCard.coupon.unlockedTag") : t("referralCard.coupon.lockedTag")}
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-display font-black text-slate-900" data-testid="referral-coupon-after">
              ${stats.premium_price_after.toFixed(2)}
            </span>
            <span className="text-lg text-slate-500 line-through" data-testid="referral-coupon-before">
              ${stats.premium_price_before.toFixed(2)}
            </span>
            <span className={`text-sm font-semibold ${
              stats.coupon_unlocked ? "text-emerald-700" : "text-amber-800"
            }`}>
              {t("referralCard.coupon.save", { savings: stats.premium_savings.toFixed(2), pct: stats.discount_percent })}
            </span>
          </div>
          {!stats.coupon_unlocked && (
            <div className="mt-1.5 text-xs text-slate-600">
              <Trans
                i18nKey="referralCard.coupon.unlockCondition"
                count={stats.remaining}
                values={{ count: stats.remaining }}
                components={{ b: <strong /> }}
              />
            </div>
          )}
        </div>
      )}

      {/* Friends who paid + completed a lesson but haven't linked Discord yet. */}
      {(stats.pending_discord_link || []).length > 0 && !stats.coupon_redeemed && (
        <div
          data-testid="referral-pending-discord"
          className="mt-4 rounded-xl border border-blue-200 bg-blue-50/60 p-4"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-blue-800">
            {t("referralCard.pending.title")}
          </div>
          <div className="mt-1 text-sm text-slate-800">
            {stats.pending_discord_link.map((f) => f.name).join(", ")}
            {t("referralCard.pending.needs", { count: stats.pending_discord_link.length })}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            <Trans i18nKey="referralCard.pending.nudge" components={{ b: <strong /> }} />
          </div>
        </div>
      )}
    </div>
  );
}
